import type { PimWorkflowScope } from "@nova-ai/shared"
import type { NutritionRecoverySession } from "../../services/use-nutrition-recovery-verification"
import { useNutritionRecovery } from "../../services/use-nutrition-recovery"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { Button } from "../ui/button"
import { NutritionRecoveryResult } from "./nutrition-recovery-result"

export function NutritionRecovery({
  scopes,
  paused,
  session,
}: {
  scopes: PimWorkflowScope[]
  paused: boolean
  session: NutritionRecoverySession
}) {
  const targetIds = [...new Set(scopes.map((scope) => scope.targetId))].slice(0, 100)
  const existingIds = scopes.flatMap((scope) => (scope.recovery ? [scope.recovery.id] : []))
  const recovery = useNutritionRecovery(targetIds, existingIds, paused, session)
  const { preview, execute, recoveries, recoveryIds } = recovery
  const eligible =
    preview.data?.targets.filter(
      (target) =>
        target.classification !== "unavailable" &&
        target.affectedScopes.length > 0 &&
        !target.resolved &&
        !target.recoveryId
    ) ?? []
  return (
    <section className="space-y-3 border-t border-border pt-5" aria-label="Exact target recovery">
      <h3 className="text-sm font-semibold">Recovery</h3>

      <p className="text-xs text-muted-foreground">{`Check eligibility for ${targetIds.length} loaded frozen targets. A target can cover multiple stores and SKUs. Load more scopes to inspect other targets.`}</p>

      <Button
        className="min-h-11"
        disabled={!targetIds.length || preview.isPending || execute.isPending}
        onClick={() => {
          execute.reset()
          preview.mutate()
        }}
      >
        {preview.isPending ? "Checking eligibility…" : "Preview eligible recovery"}
      </Button>

      {preview.error && (
        <p role="alert" className="text-sm text-destructive">
          {preview.error.message}
        </p>
      )}

      {preview.data?.targets.map((target) => (
        <div key={target.targetId} className="space-y-1 text-xs">
          <p className="font-medium">{`Target #${target.targetId} · ${target.store} · ${target.simpleSku}`}</p>

          <p>
            {humanizeEnumLabel(target.classification)}

            {target.resolved ? " · already resolved" : ""}
          </p>

          {target.blockers.map((blocker) => (
            <p key={blocker} className="break-words text-warning">
              {humanizeEnumLabel(blocker)}
            </p>
          ))}

          {target.affectedScopes.length > 0 && (
            <p className="break-words">
              {`All affected scopes: ${target.affectedScopes.map((scope) => `${scope.store} / ${scope.sku} (product ${scope.productId ?? "unknown"})`).join("; ")}`}
            </p>
          )}

          {target.classification === "unavailable" && (
            <p>Recovery is unsupported for this evidence. Investigate the recorded cause.</p>
          )}
        </div>
      ))}

      {eligible.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-warning">
            Recovery can affect every listed scope, including stores outside the selected row.
            Preview evidence is checked again at admission.
          </p>

          <Button
            className="min-h-11"
            variant="primary"
            disabled={execute.isPending || execute.isSuccess || !!execute.error}
            onClick={() => execute.mutate()}
          >
            {execute.isPending
              ? "Admitting recovery…"
              : `Recover ${eligible.length} eligible targets`}
          </Button>
        </div>
      )}

      {execute.error && (
        <p
          role="alert"
          className="text-sm text-destructive"
        >{`${execute.error.message} Preview again before retrying. Any admitted targets remain visible below.`}</p>
      )}

      <p className="text-xs text-muted-foreground">
        Admitted recovery verifies every 15 seconds while this panel is open and visible.
        Verification stops after five minutes or 20 checks, a blocker, or an error. Closing this
        panel pauses advancement. Workers already started continue.
      </p>

      {recoveries.map((_query, index) => (
        <NutritionRecoveryResult
          key={recoveryIds[index]}
          recovery={recovery}
          index={index}
          paused={paused}
        />
      ))}

      {paused && recoveries.length > 0 && (
        <p className="text-xs text-warning">
          Recovery verification and readback are paused. Workers already started continue.
        </p>
      )}
    </section>
  )
}
